import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router';
import moment from 'moment';
import { connect } from 'react-redux';
import Profile from '@common/components/profile';
import Card from '@common/components/card';
import Icon from '@common/components/icon';
import Dropdown from '@common/components/dropdown';
import ConfirmButton from '@common/components/confirm-button';
import { EParentTypes } from '@common/definitions';
import type { LoggedUser } from '@common/types/objects';
import type { Message } from '../../../../types/objects';
import RecognitionCard from '@common/components/recognition-card/recognition-card';
import Tooltip from '@common/components/tooltip';
import { Link } from 'react-router-dom';
import LinkWrapper from '@common/components/linkWrapper';


type Props = RouteComponentProps & {
  loggedUser: LoggedUser;
  item: Message;
  shouldHideParent: boolean;
  onDelete: (id: string) => Promise<void>;
  onToggleImportant: (id: string, important: boolean) => Promise<void>;
  onToggleComments: (messageId: string, enabled: boolean) => Promise<void>;
  onToggleMute: (id: string) => Promise<void>;
  onMarkAsRead: () => void;
  network?: string | null;
};

const DefaultMessageContentComponent = ({
  item,
  loggedUser,
  shouldHideParent,
  onMarkAsRead,
  onToggleImportant,
  onToggleComments,
  onToggleMute,
  onDelete,
  network,
  match,
}: Props) => {
  const { t } = useTranslation();
  const { user, recognition } = item;
  const networkId = item.created_in?.type === EParentTypes.TEAM ?
  item.created_in?.parent?.networkId as string : item.created_in?.id;

  const showDropdown = item.actions?.can_edit
    || item.actions?.can_mute
    || item.actions?.can_mark_important
    || item.actions?.can_delete;

  const isChannel = match.path === '/networks/:networkId/messages/:type/:id' &&
    // @ts-expect-error
    match?.params?.type === 'channel';

  const getIconDisplay = () => {
    switch (item.created_in?.type) {
      case EParentTypes.CHANNEL:
      case EParentTypes.ORGANISATION:
        return (
          <div className="icon-active primary tw-rounded-full  tw-p-[3px] tw-absolute tw-right-[-5px] tw-bottom-0">
            <Icon key="icon" type="bag" className="!tw-w-2.5 !tw-h-2.5  tw-text-white" />
          </div>);
      case EParentTypes.NETWORK:
      case EParentTypes.TEAM:
        return (
          <div className="icon-active tw-rounded-full  tw-p-[3px] tw-absolute tw-right-[-5px] tw-bottom-0">
            <Icon key="icon" type="community_icon" className="!tw-w-2.5 !tw-h-2.5 tw-text-white" />
          </div>);
      case EParentTypes.EVENT:
        return (
          <div className="icon-active event-icon tw-rounded-full  tw-p-[3px] tw-absolute tw-right-[-5px] tw-bottom-0">
            <Icon key="icon" type="calendar_today" className="!tw-w-2.5 !tw-h-2.5 tw-text-white" />
          </div>);
        default:
        return null;
    }
  };

  const defaultNetworkId = item.created_in?.type === EParentTypes.TEAM ? item.created_in?.parent?.networkId as string :
  // @ts-expect-error
  match?.params?.networkId;
  const subHeaderLink = item.created_in?.type === EParentTypes.EVENT ?
  `/networks/${defaultNetworkId}/events/${item.created_in?.id}`
  : `/networks/${defaultNetworkId}/messages/${item.created_in?.type}/${item.created_in?.id}`;

  const WrapLinkComponent = (children:ReactNode) => (
    <LinkWrapper
      className="tw-text-inherit hover:tw-text-inherit"
      url={'/messages/' + item.id}
    >{children}
    </LinkWrapper>);

  return (
    <div className="Message__Inner Message--type-default">
      <Card.Header>
        <div className="Message__header__inner">
          <div className="pull-right">
            {item.is_important && <Icon type="star__filled" className="Message__Important" />}
            {showDropdown && (
              <Dropdown
                alignRight
                id={`Message__Actions-${item.id}`}
                toggle={<Icon type="more_vert" size="small" />}
              >
                {item.actions.can_edit && item.message_type !== 'recognition_message' && (
                  <Dropdown.Item>
                    {t('social:feed_item_message_edit_message')}
                  </Dropdown.Item>
                )}
                {item.is_important && !item.has_read && (
                  <Dropdown.Item onClick={onMarkAsRead}>
                    {t('social:feed_item_message_mark_as_read')}
                  </Dropdown.Item>
                )}
                {!item.publish_at && item.actions.can_mute && (
                  <Dropdown.Item onClick={() => onToggleMute(item.id)}>
                    {!item.has_muted
                      ? t('social:feed_item_message_mute')
                      : t('social:feed_item_message_unmute')}
                  </Dropdown.Item>
                )}
                {!item.publish_at && item.actions.can_mark_important && (item.is_important ? (
                  <ConfirmButton
                    danger
                    key="remove"
                    title={t('social:feed_item_message_default_confirm_remove_important_title')}
                    onConfirm={() => onToggleImportant(item.id, false)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_remove_important')}</Dropdown.Item>
                  </ConfirmButton>
                ) : (
                  <ConfirmButton
                    key="mark"
                    title={t('social:feed_item_message_default_confirm_mark_as_important_title')}
                    onConfirm={() => onToggleImportant(item.id, true)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_mark_as_important')}</Dropdown.Item>
                  </ConfirmButton>
                ))}
                {item.actions.can_disable_comments && (item.can_comment ? (
                  <ConfirmButton
                    danger
                    key="disable_comments"
                    title={t('social:feed_item_message_default_confirm_disable_comments_title')}
                    onConfirm={() => onToggleComments(item.id, false)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_disable_comments')}</Dropdown.Item>
                  </ConfirmButton>
                ) : (
                  <ConfirmButton
                    key="enable_comments"
                    title={t('social:feed_item_message_default_confirm_enable_comments_title')}
                    onConfirm={() => onToggleComments(item.id, true)}
                  >
                    <Dropdown.Item>{t('social:feed_item_message_enable_comments')}</Dropdown.Item>
                  </ConfirmButton>
                ))}
                {item.actions.can_delete && (
                  <ConfirmButton
                    danger
                    title={t('social:feed_item_message_default_confirm_remove_title')}
                    description={t('social:feed_item_message_default_confirm_remove_description')}
                    onConfirm={() => onDelete(item.id)}
                    confirmText={t('social:feed_item_message_default_confirm_remove')}
                  >
                    <Dropdown.Item danger>{t('social:feed_item_message_default_confirm_remove')}</Dropdown.Item>
                  </ConfirmButton>
                )}
              </Dropdown>
            )}
          </div>

          <Profile
            user={user}
            meta={!shouldHideParent && (
              <>
                {item.created_in?.type !== EParentTypes.NETWORK && item.created_in?.type !== EParentTypes.ORGANISATION && (
                <>
                  <Icon type="chevron_right" />
                  <Link
                    className="tw-cursor-pointer tw-text-inherit hover:tw-text-inherit"
                    to={subHeaderLink}
                  >{item.created_in?.values}
                  </Link>
                </>
              )}
              </>
            )}
            enablePopover={item.user_id !== loggedUser.id && !isChannel}
            showExtraIcon={getIconDisplay()}
            nameWrapper={WrapLinkComponent}
          >
            <div>
              <small className={item.publish_at && 'Message__PublishAt'}>
                {(item.created_in?.type === EParentTypes.TEAM || item.created_in?.type === EParentTypes.NETWORK) && (
                <Link
                  className="message-meta-item network tw-cursor-pointer"
                  to={`/networks/${networkId}/messages/network`}
                >
                  {item.created_in?.parent?.name}
                </Link>
                )}
                {(item.created_in?.type === EParentTypes.CHANNEL) && (
                  <span
                    className="message-meta-item organisation"

                  >
                    {item.created_in?.parent?.organisationName}
                  </span>
                )}
                <span className="!tw-mr-0">·</span>
                <Tooltip title={item?.created_at ? moment(item.created_at).format('LLLL') : ''}>
                  <span>
                    <Icon key="icon" type="access_time" />
                    {item?.publish_at ? (
                    t('social:feed_item_message_default_planned_at', {
                      datetime: moment(item.publish_at).format(`dddd D MMMM [${t('social:feed_item_message_default_at')}] LT`),
                    })
                  ) : item?.created_at ? moment(item.created_at).calendar() : ''}
                  </span>
                </Tooltip>
              </small>
              {item.created_in?.type === EParentTypes.EVENT && (
                <>
                  <span>·</span>
                  <small>
                    <Icon key="icon" type="event" />
                    {t('social:feed_item_message_event')}
                  </small>
                </>
              )}
            </div>
          </Profile>
        </div>
      </Card.Header>

      <RecognitionCard
        loggedUser={loggedUser}
        recognition={recognition}
        isChannel={isChannel}
        network={network}
        user={recognition && recognition.receiver}
        match={match}
        enablePopovers
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  network: state.network.selected,
});

export default connect(mapStateToProps)(withRouter(DefaultMessageContentComponent));
